import {
  type ColumnDef,
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  type InitialTableState,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cva, type VariantProps } from "class-variance-authority";
import {
  memo,
  type ReactElement,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "../../utils";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../table";
import { DataTableHeaderCell } from "./data-table-header-cell";
import { MemoizedTableRow } from "./data-table-row";
import {
  distributeColumnWidths,
  getShortfallTableWidth,
} from "./distribute-column-widths";
import type { IFilterOption } from "./filters/MultiSelectFilterInput";
import type { FiltersState } from "./filters/types";

export type ColumnFilterType =
  | "text"
  | "date"
  | "multiSelect"
  | "numberRange"
  | "creatableMultiSelect";

const dataTableVariants = cva(
  [
    "table-fixed border-separate border-spacing-0 [&_td]:border-gray-200 [&_tfoot_td]:border-t [&_th]:border-b [&_th]:border-gray-300 [&_tr:not(:last-child)_td]:border-b [&_tr]:border-none",
  ].join(" "),
  {
    variants: {
      spacing: {
        sm: "[&_th]:px-1 [&_th]:py-2 [&_td]:px-1 [&_td]:py-2",
        md: "[&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-4",
        lg: "[&_th]:px-3 [&_th]:py-6 [&_td]:px-3 [&_td]:py-6",
      },
    },
    defaultVariants: {
      spacing: "md",
    },
  },
);

const surfaceVariants = cva("overflow-hidden rounded-md bg-white", {
  variants: {
    surface: {
      bordered: "border border-gray-200",
      borderless: "",
      shadow: "shadow-lg p-4",
    },
  },
});

export type ColumnType = "string" | "number" | "boolean" | "date";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
    /** Human-readable column label used in sort/filter badges. */
    label?: string;
    /**
     * Sizes this column as a percentage of the table (`stretch: 25` → `width: 25%`) instead of
     * the default: `size` plus a proportional share of the spare width, capped at `maxSize`
     * (see `distributeColumnWidths`). Give the percentages of a table using this form a sum of
     * 100 — any percentage column switches the whole table to this mode. Has no effect on a
     * pinned column.
     */
    stretch?: number;
    filterType?: ColumnFilterType;
    /** Placeholder for a text filter's search box (defaults to "Search…"). */
    filterPlaceholder?: string;
    columnType?: ColumnType;
    /** Options for multiSelect filters. `label` may be a ReactNode for rich display. */
    filterOptions?: IFilterOption[];
  }
}

interface IDataTableProps<TData extends RowData>
  extends Pick<
      TableOptions<TData>,
      | "onRowSelectionChange"
      | "onSortingChange"
      | "getRowId"
      | "onColumnVisibilityChange"
      | "onColumnOrderChange"
    >,
    VariantProps<typeof dataTableVariants>,
    VariantProps<typeof surfaceVariants> {
  columns: ColumnDef<TData>[];
  data: TData[];
  sorting?: SortingState;
  selection?: RowSelectionState;
  columnVisibility?: VisibilityState;
  /** Column ids in display order — an id absent from this array renders after the ordered ones,
   * in column-definition order (TanStack's own fallback), so a stale stored order never hides a
   * newly added column. */
  columnOrder?: string[];
  enableRowSelection?: boolean;
  enableMultiSort?: boolean;
  initialTableState?: InitialTableState;
  getRowClassName?: (row: TData) => string | undefined;
  /** When provided, whole rows become clickable (cursor-pointer + onClick), not just a cell. */
  onRowClick?: (row: TData) => void;
  /**
   * Router-agnostic row URL. When it returns a string, the row's first cell renders a real
   * `<a href>` so ctrl/cmd/middle-click open a new tab; a plain click still calls `onRowClick`.
   * Pass a stable reference (module scope or `useCallback`) so row memoization holds.
   */
  getRowHref?: (row: TData) => string | undefined;
  /** Enables row virtualization for large lists. */
  virtualRows?: boolean;
  /** Active column filters keyed by column id. */
  filters?: FiltersState;
  /** Called whenever a column filter changes. */
  onFiltersChange?: (filters: FiltersState) => void;
  /**
   * Optional renderer for an expanded row's detail panel. When provided, rows
   * become expandable and `row.getToggleExpandedHandler()` can be wired to a
   * toggle (typically a chevron column) inside `columns`.
   */
  renderExpandedRow?: (row: Row<TData>) => ReactNode;

  className?: string;
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  sorting,
  selection = {},
  enableRowSelection = false,
  enableMultiSort = false,
  initialTableState,
  getRowClassName,
  onRowClick,
  getRowHref,
  virtualRows = false,
  filters,
  onFiltersChange,
  columnVisibility,
  columnOrder,
  renderExpandedRow,
  spacing = "md",
  surface = "shadow",
  className,
  ...rest
}: IDataTableProps<TData>) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const expandable = !!renderExpandedRow;
  const table = useReactTable<TData>({
    data,
    columns,
    state: {
      sorting,
      rowSelection: selection,
      columnVisibility,
      columnOrder,
      ...(expandable ? { expanded } : {}),
    },
    initialState: initialTableState,
    enableRowSelection,
    enableMultiSort,
    enableSorting: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    ...(expandable
      ? {
          onExpandedChange: setExpanded,
          getExpandedRowModel: getExpandedRowModel(),
          getRowCanExpand: () => true,
        }
      : {}),
    ...rest,
  });

  const rootRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  // Stable ref to the scrollable ancestor; set once after mount.
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const rows = table.getRowModel().rows;

  const useVirtual = virtualRows && !expandable;

  // Every column gets an explicit px width that together fill the container: left to itself, a
  // `table-fixed` table spreads spare width over ALL columns, sticky pinned ones included, while
  // their `left`/`right` offsets come from the declared sizes — so they would overlap.
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  // TanStack memoizes this array until the columns, their order or visibility change — but NOT
  // pinning, which a header menu can change at runtime, so that is a dependency of its own. Keeps
  // the widths (and the memoized rows that receive them) stable until the layout really changes.
  const visibleColumns = table.getVisibleLeafColumns();
  const { columnPinning } = table.getState();
  const columnWidths = useMemo(
    () =>
      distributeColumnWidths(
        visibleColumns.map((column) => {
          const stretch = column.columnDef.meta?.stretch;
          return {
            id: column.id,
            size: column.getSize(),
            maxSize: column.columnDef.maxSize,
            pinned:
              !!columnPinning.left?.includes(column.id) ||
              !!columnPinning.right?.includes(column.id),
            stretchPercent: typeof stretch === "number" ? stretch : undefined,
          };
        }),
        containerWidth,
      ),
    [visibleColumns, columnPinning, containerWidth],
  );
  const tableWidth = getShortfallTableWidth(columnWidths, containerWidth);

  // After mount, walk up the DOM from the DataTable's outer div to find the
  // first scrollable ancestor. Starting from outside DataTable avoids the
  // internal Table wrapper div whose overflow-auto (needed for horizontal
  // scroll) would otherwise be incorrectly selected as the vertical scroll
  // container. The scrollHeight > clientHeight guard is intentionally omitted:
  // it creates a circular dependency where an empty virtual table prevents the
  // container from ever being found.
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    if (!useVirtual || !rootRef.current || !tableBodyRef.current) return;
    let el: Element | null = rootRef.current.parentElement;
    while (el && el !== document.body) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") {
        scrollContainerRef.current = el as HTMLElement;
        break;
      }
      el = el.parentElement;
    }
    const container = scrollContainerRef.current;
    if (!container) return;
    const { top: containerTop } = container.getBoundingClientRect();
    const { top: tbodyTop } = tableBodyRef.current.getBoundingClientRect();
    setScrollMargin(tbodyTop - containerTop + container.scrollTop);
  }, [useVirtual]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 65,
    overscan: 12,
    getScrollElement: () => scrollContainerRef.current,
    scrollMargin,
    enabled: useVirtual,
  });

  const virtualItems = useVirtual ? virtualizer.getVirtualItems() : [];
  const totalVirtualSize = useVirtual ? virtualizer.getTotalSize() : 0;
  const paddingTop =
    virtualItems.length > 0
      ? Math.max(0, (virtualItems[0]?.start ?? 0) - scrollMargin)
      : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalVirtualSize - (virtualItems[virtualItems.length - 1]?.end ?? 0)
      : 0;

  return (
    <div ref={rootRef} className={cn(surfaceVariants({ surface }), className)}>
      <Table
        className={cn(dataTableVariants({ spacing }))}
        style={tableWidth === undefined ? undefined : { width: tableWidth }}
      >
        <TableHeader>
          {table
            .getHeaderGroups()
            // With every column of a group hidden, its header row is only placeholders — skip it
            // rather than render an empty band above the real headers.
            .filter((headerGroup) =>
              headerGroup.headers.some((header) => !header.isPlaceholder),
            )
            .map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="bg-transparent! hover:bg-transparent!"
              >
                {headerGroup.headers.map((header) => (
                  <DataTableHeaderCell
                    key={header.id}
                    header={header}
                    sorting={sorting}
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    onSortingChange={rest.onSortingChange}
                    columnWidths={columnWidths}
                  />
                ))}
              </TableRow>
            ))}
        </TableHeader>
        <TableBody ref={tableBodyRef}>
          {rows.length ? (
            useVirtual ? (
              <>
                {paddingTop > 0 && (
                  <tr aria-hidden style={{ height: paddingTop }}>
                    <td
                      colSpan={columns.length}
                      style={{ padding: 0, border: 0 }}
                    />
                  </tr>
                )}
                {virtualItems.map((vRow) => {
                  const row = rows[vRow.index] as Row<TData>;
                  if (!row) return null;
                  return (
                    <ExpandableRowGroup
                      key={row.id}
                      row={row}
                      enableRowSelection={enableRowSelection}
                      getRowClassName={getRowClassName}
                      onRowClick={onRowClick}
                      getRowHref={getRowHref}
                      renderExpandedRow={renderExpandedRow}
                      isSelected={row.getIsSelected()}
                      isExpanded={row.getIsExpanded()}
                      columnVisibility={columnVisibility}
                      columnWidths={columnWidths}
                    />
                  );
                })}
                {paddingBottom > 0 && (
                  <tr aria-hidden style={{ height: paddingBottom }}>
                    <td
                      colSpan={columns.length}
                      style={{ padding: 0, border: 0 }}
                    />
                  </tr>
                )}
              </>
            ) : (
              rows.map((row) => (
                <ExpandableRowGroup
                  key={row.id}
                  row={row}
                  enableRowSelection={enableRowSelection}
                  getRowClassName={getRowClassName}
                  onRowClick={onRowClick}
                  getRowHref={getRowHref}
                  renderExpandedRow={renderExpandedRow}
                  isSelected={row.getIsSelected()}
                  isExpanded={row.getIsExpanded()}
                  columnVisibility={columnVisibility}
                  columnWidths={columnWidths}
                />
              ))
            )
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface IExpandableRowGroupProps<TData extends RowData> {
  row: Row<TData>;
  enableRowSelection: boolean;
  getRowClassName?: (row: TData) => string | undefined;
  onRowClick?: (row: TData) => void;
  getRowHref?: (row: TData) => string | undefined;
  renderExpandedRow?: (row: Row<TData>) => ReactNode;
  // Primitive signals for React.memo — row object reference is stable so memo
  // can't detect selection/expansion/visibility changes without these explicit props.
  isSelected: boolean;
  isExpanded: boolean;
  columnVisibility?: VisibilityState;
  columnWidths?: Record<string, number> | null;
}

const ExpandableRowGroup = memo(function ExpandableRowGroup<
  TData extends RowData,
>({
  row,
  enableRowSelection,
  getRowClassName,
  onRowClick,
  getRowHref,
  renderExpandedRow,
  isSelected,
  isExpanded,
  columnVisibility,
  columnWidths,
}: IExpandableRowGroupProps<TData>) {
  const colSpan = row.getVisibleCells().length;
  return (
    <>
      <MemoizedTableRow
        row={row}
        enableRowSelection={enableRowSelection}
        getRowClassName={getRowClassName}
        onRowClick={onRowClick}
        getRowHref={getRowHref}
        isExpanded={isExpanded}
        isSelected={isSelected}
        columnVisibility={columnVisibility}
        columnWidths={columnWidths}
      />
      {renderExpandedRow && isExpanded && (
        <TableRow className="bg-white">
          <TableCell colSpan={colSpan} className="bg-white px-4 py-3">
            {renderExpandedRow(row)}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}) as <TData extends RowData>(
  props: IExpandableRowGroupProps<TData>,
) => ReactElement;
